import { useMemo, useState } from 'react';
import { Alert, Breadcrumb, Button, Flex, Skeleton, theme, Tree } from 'antd';
import styled from 'styled-components';

import BaseText from '@components/typography/BaseText';
import { collectUuids } from '@routes/userRoutes/objects/objectTree';

import { indexByUuid, toggleNode } from '../collectionTree';
import BuilderTreeCard from './BuilderTreeCard';

// Card rows are taller than antd's single-line nodes: space the rows apart and
// center the caret on the card.
const treeStyles = gap => ({
	item: { marginBottom: gap },
	itemSwitcher: { alignSelf: 'center' },
});

// antd has no semantic style for the checkbox and pins it near the top of the
// row, so center it here. Tied to antd v6's class name — review on upgrades.
const TreeWrapper = styled.div`
	.ant-tree-checkbox {
		align-self: center;
		margin-block-start: 0;
	}
`;

// Breadcrumb has no size prop; the back link sits at the crumb's own size.
const SmallBreadcrumb = styled(Breadcrumb)`
	font-size: ${({ theme }) => theme.token.fontSizeXS}px;
`;

// A link-styled button that reads as a crumb: no inset, the crumb's height and size.
const CrumbButton = styled(Button)`
	&& {
		padding: 0;
		height: auto;
		font-size: inherit;
	}
`;

// Nodes are identified by uuid. Their `key` is the taxonomy slug, which collections don't use.
const TREE_FIELD_NAMES = { key: 'uuid', title: 'name' };

/**
 * The builder's left column: the breadcrumb back to the list, and the taxonomy
 * tree. Each object is picked on its own (`checkStrictly`): a parent's checkbox
 * never picks its children, and a node without a model is disabled. Clicking a
 * card toggles it like its checkbox.
 *
 * @param {Array} props.tree - the taxonomy for this project type, already pruned
 *   to pickable nodes and filtered by the search
 * @param {boolean} props.isSearching - a search expands every match
 * @param {boolean} [props.backDisabled] - a save is in flight, so the builder can't be left yet
 * @param {boolean} props.isEditing - read-only: a collection's objects are fixed once
 *   it is created, so the tree only shows what it holds
 */
const CollectionBuilder = ({
	tree,
	mode,
	pickedNodeUuids,
	onPick,
	isSearching,
	isEditing,
	isLoading,
	isError,
	onBack,
	backDisabled = false,
}) => {
	const { token } = theme.useToken();

	// null = no manual expand/collapse yet → everything expanded.
	const [expandedKeys, setExpandedKeys] = useState(null);

	const allKeys = useMemo(() => collectUuids(tree), [tree]);
	const nodesByUuid = useMemo(() => indexByUuid(tree), [tree]);

	const pickedUuids = useMemo(() => new Set(pickedNodeUuids), [pickedNodeUuids]);

	// A search reveals every match, without overwriting what the user had open.
	const effectiveExpandedKeys = isSearching ? allKeys : (expandedKeys ?? allKeys);

	const handleExpand = keys => {
		if (!isSearching) setExpandedKeys(keys);
	};

	const pick = eventNode => {
		const node = nodesByUuid.get(eventNode.uuid)?.node;
		if (node) onPick(toggleNode(node, pickedNodeUuids));
	};

	const renderTitle = node => (
		<BuilderTreeCard object={node} mode={mode} checked={pickedUuids.has(node.uuid)} />
	);

	const renderBody = () => {
		if (isLoading) return <Skeleton active title={false} paragraph={{ rows: 6 }} />;
		if (isError)
			return (
				<Alert
					type="error"
					showIcon
					title="Couldn't load objects"
					description="Please try again later."
				/>
			);
		if (tree.length === 0)
			return (
				<BaseText
					size="sm"
					type="secondary"
					style={{ display: 'block', textAlign: 'center', marginBlock: token.marginXXL }}>
					{isSearching
						? 'No objects match this search.'
						: 'No objects can be detected for this project type yet.'}
				</BaseText>
			);

		return (
			<TreeWrapper>
				<Tree
					checkable
					checkStrictly
					disabled={isEditing}
					styles={treeStyles(token.marginXS)}
					blockNode
					fieldNames={TREE_FIELD_NAMES}
					treeData={tree}
					titleRender={renderTitle}
					expandedKeys={effectiveExpandedKeys}
					onExpand={handleExpand}
					checkedKeys={pickedNodeUuids}
					onCheck={(_keys, { node }) => pick(node)}
					selectedKeys={[]}
					onSelect={(_keys, { node }) => pick(node)}
				/>
			</TreeWrapper>
		);
	};

	return (
		<Flex vertical gap="small">
			<SmallBreadcrumb
				items={[
					{
						title: (
							<CrumbButton
								type="link"
								size="small"
								disabled={backDisabled}
								onClick={onBack}>
								Collections
							</CrumbButton>
						),
					},
					{ title: isEditing ? 'Edit collection' : 'New collection' },
				]}
			/>
			{renderBody()}
		</Flex>
	);
};

export default CollectionBuilder;
